/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  IconButton,
  Chip,
} from "@material-ui/core";
import AddCircle from "@material-ui/icons/AddCircle";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

import DeleteDialog from "../../../components/dialog-delete/dialog-delete.view";
import ShowNotification from "../../../components/react-notifications/react-notifications";
import { NotificationMessageType } from "../../../utils/configuration";
import * as viVN from "../../../language/vi-VN.json";
import { getInvestmentErrorMessage } from "../../../common/tools";
import * as masterAction from "../../../redux/store/investment-opportunity/investment-master.store";

import InvestmentMasterForm from "./investment-master-form.view";

const useStyles = makeStyles(() => ({
  tableContainer: {
    maxHeight: window.outerHeight - 300,
  },
  table: {
    minWidth: 750,
  },
}));

/**
 * Màn hình CRUD dùng chung cho Danh mục dự án / Trạng thái dự án / Hình thức đầu tư
 * @param kind tiền tố API (InvestmentMasterKind)
 * @param title tên loại, ví dụ "danh mục dự án"
 * @param hasSlug chỉ Danh mục dự án có slug
 */
const InvestmentMaster = ({ kind, title, hasSlug = false }) => {
  const classes = useStyles();
  const [data, setData] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    getList();
  }, [kind]);

  const getList = () => {
    masterAction
      .GetListMaster(kind)
      .then((res) => {
        setData((res && res.content) || []);
        setIsLoaded(true);
      })
      .catch((err) => {
        setIsLoaded(true);
        ShowNotification(
          getInvestmentErrorMessage(err),
          NotificationMessageType.Error
        );
      });
  };

  const handleOpenAdd = () => {
    setEditId(null);
    setOpenForm(true);
  };

  const handleOpenEdit = (id) => {
    setEditId(id);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setEditId(null);
  };

  const handleSaved = () => {
    handleCloseForm();
    getList();
  };

  const handleDelete = () => {
    masterAction
      .DeleteMaster(kind, deleteId)
      .then((res) => {
        setDeleteId(null);
        if (res && res.content && res.content.status) {
          ShowNotification(
            viVN.Success.InvestmentMasterDeleteSuccess,
            NotificationMessageType.Success
          );
          getList();
        }
      })
      .catch((err) => {
        setDeleteId(null);
        ShowNotification(
          getInvestmentErrorMessage(err),
          NotificationMessageType.Error
        );
      });
  };

  const normalizedKeyword = keyword.trim().toLowerCase();
  const filteredData = normalizedKeyword
    ? data.filter(
        (item) =>
          (item.code || "").toLowerCase().includes(normalizedKeyword) ||
          (item.name || "").toLowerCase().includes(normalizedKeyword)
      )
    : data;
  const columnCount = hasSlug ? 8 : 7;

  return (
    <div className="slider">
      <div className="d-sm-flex align-items-center justify-content-between mb-3">
        <TextField
          size="small"
          variant="outlined"
          placeholder="Tìm theo mã hoặc tên"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          style={{ minWidth: 300 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenAdd}
          startIcon={<AddCircle />}
        >
          Thêm {title}
        </Button>
      </div>

      <Paper>
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table} size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className="pt-3 pb-3 text-nowrap">STT</TableCell>
                <TableCell className="pt-3 pb-3 text-nowrap">Mã</TableCell>
                <TableCell className="pt-3 pb-3 text-nowrap">Tên</TableCell>
                {hasSlug && (
                  <TableCell className="pt-3 pb-3 text-nowrap">Slug</TableCell>
                )}
                <TableCell className="pt-3 pb-3">Mô tả</TableCell>
                <TableCell className="pt-3 pb-3 text-nowrap" align="center">
                  Thứ tự
                </TableCell>
                <TableCell className="pt-3 pb-3 text-nowrap" align="center">
                  Trạng thái
                </TableCell>
                <TableCell className="pt-3 pb-3" />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length > 0 ? (
                filteredData.map((row, index) => (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={row.id}
                    style={{
                      backgroundColor: index % 2 ? "#FFFFFF" : "#EEF0F2",
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell className="text-nowrap">{row.code}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    {hasSlug && <TableCell>{row.slug}</TableCell>}
                    <TableCell>{row.description}</TableCell>
                    <TableCell align="center">{row.sortOrder}</TableCell>
                    <TableCell align="center">
                      {row.isActive ? (
                        <Chip size="small" color="primary" label="Hoạt động" />
                      ) : (
                        <Chip size="small" label="Ngừng" />
                      )}
                    </TableCell>
                    <TableCell align="right" className="text-nowrap">
                      <Tooltip title="Sửa">
                        <IconButton
                          aria-label="edit"
                          onClick={() => handleOpenEdit(row.id)}
                        >
                          <EditIcon className="text-primary" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Xóa">
                        <IconButton
                          aria-label="delete"
                          onClick={() => setDeleteId(row.id)}
                        >
                          <DeleteIcon className="text-danger" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow hover tabIndex={-1}>
                  <TableCell colSpan={columnCount} className="text-center">
                    {isLoaded ? "Không có dữ liệu" : "Đang tải..."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {openForm && (
        <InvestmentMasterForm
          isOpen={openForm}
          onClose={handleCloseForm}
          onSuccess={handleSaved}
          kind={kind}
          title={title}
          hasSlug={hasSlug}
          id={editId}
        />
      )}

      {deleteId && (
        <DeleteDialog
          isOpen={!!deleteId}
          onClose={() => setDeleteId(null)}
          onSuccess={handleDelete}
          header={"Xác nhận xóa"}
          content={"Bạn có chắc chắn muốn xóa?"}
        />
      )}
    </div>
  );
};

export default InvestmentMaster;
